import { Affix, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { initialAppUpdateState, startAppUpdates } from '@/lib/pwa-updates';

export default function AppUpdatePrompt() {
  const [update, setUpdate] = useState(initialAppUpdateState);
  const controller = useRef<ReturnType<typeof startAppUpdates>>();

  useEffect(() => {
    const updates = startAppUpdates({
      workerUrl: `${import.meta.env.BASE_URL}${import.meta.env.DEV ? 'dev-sw.js?dev-sw' : 'sw.js'}`,
      onChange: setUpdate,
      onRegistrationError: (error) => console.error('Unable to register app updates:', error),
    });
    controller.current = updates;
    return () => updates.stop();
  }, []);

  if (!update.available) return null;

  return (
    <Affix
      position={{ bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))', right: 16 }}
      w={{ base: 'calc(100% - 32px)', sm: 400 }}
      zIndex={1000}
    >
      <Paper withBorder shadow="md" radius="md" p="md" role="status" aria-live="polite">
        <Stack gap="xs">
          <Text fw={600}>{update.activeUpdate ? 'App refresh available' : 'Update available'}</Text>
          <Text size="sm">
            {update.activeUpdate
              ? 'Save your work, then reload this window to use the current version of ParcelQuest.'
              : 'A new version of ParcelQuest is ready. Save your work, then update to reload the app.'}
          </Text>
          {update.error && <Text size="sm" c="red" role="alert">{update.error}</Text>}
          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              disabled={update.updating}
              onClick={() => controller.current?.dismiss()}
            >
              Later
            </Button>
            <Button color="pq-red" loading={update.updating} onClick={() => void controller.current?.applyUpdate()}>
              {update.activeUpdate ? 'Reload now' : 'Update now'}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Affix>
  );
}
